import React, { useState } from 'react';
import { ChevronLeft, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { getDateLocale, getTexts } from '../utils/i18n';
import '../styles/appointmentCalendar.css';

interface AppointmentCalendarProps {
    messageType: number;
    scheduleData: Record<string, string[]>;
    onAppointmentSelect?: (dateStr: string, time: string) => void;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
    scheduleData,
    onAppointmentSelect
}) => {
    const texts = getTexts();
    const dateLocale = getDateLocale();
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [currentView, setCurrentView] = useState<'dates' | 'slots'>('dates');
    const [showAllDates, setShowAllDates] = useState<boolean>(false);

    const MAX_INITIAL_DATES = 12;

    const getDayName = (dateStr: string) => {
        const [day, month, year] = dateStr.split('.');
        const date = new Date(`${year}-${month}-${day}`);
        return date.toLocaleDateString(dateLocale, { weekday: 'long' });
    };

    const getFormattedDate = (dateStr: string) => {
        const [day, month, year] = dateStr.split('.');
        const date = new Date(`${year}-${month}-${day}`);
        return date.toLocaleDateString(dateLocale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const sortedDates = Object.keys(scheduleData).sort((a, b) => {
        const [d1, m1, y1] = a.split('.').map(Number);
        const [d2, m2, y2] = b.split('.').map(Number);
        return new Date(y1, m1 - 1, d1).getTime() - new Date(y2, m2 - 1, d2).getTime();
    });

    // Gösterilecek tarihleri belirle
    const datesToShow = showAllDates ? sortedDates : sortedDates.slice(0, MAX_INITIAL_DATES);
    const hasMoreDates = sortedDates.length > MAX_INITIAL_DATES;

    const handleDateSelect = (dateStr: string) => {
        setSelectedDate(dateStr);
        setCurrentView('slots');
    };

    const handleTimeButtonClick = (time: string) => {
        if (onAppointmentSelect && selectedDate) {
            onAppointmentSelect(selectedDate, time);
        }
    };

    const handleBackToDateSelection = () => {
        setCurrentView('dates');
        setSelectedDate(null);
    };

    const toggleShowAllDates = () => {
        setShowAllDates(!showAllDates);
    };

    const isDateAvailable = (dateStr: string) => {
        return scheduleData[dateStr] && scheduleData[dateStr].length > 0;
    };

    if (currentView === 'dates') {
        return (
            <div className="calendar-wrapper">
                <div className="calendar-header">
                    <div className="calendar-header-icon">
                        <Calendar size={20} />
                    </div>
                    <div>
                        <h3 className="calendar-header-title">{texts.selectAppointmentDate}</h3>
                        <p className="calendar-header-subtitle">
                            {showAllDates
                                ? texts.showingAllDates(sortedDates.length)
                                : texts.showingVisibleDates(Math.min(MAX_INITIAL_DATES, sortedDates.length), sortedDates.length)
                            }
                        </p>
                    </div>
                </div>

                <div className="date-selection-grid">
                    {datesToShow.map((dateStr, index) => {
                        const isAvailable = isDateAvailable(dateStr);
                        const slotCount = scheduleData[dateStr]?.length || 0;

                        return (
                            <div
                                key={index}
                                className={`date-card ${isAvailable ? 'available' : 'unavailable'}`}
                                onClick={() => isAvailable && handleDateSelect(dateStr)}
                            >
                                <div className="date-card-day">
                                    {getDayName(dateStr)}
                                </div>
                                <div className="date-card-date">
                                    {dateStr.split('.')[0]}
                                </div>
                                <div className="date-card-month">
                                    {new Date(`${dateStr.split('.')[2]}-${dateStr.split('.')[1]}-${dateStr.split('.')[0]}`).toLocaleDateString(dateLocale, { month: 'short' })}
                                </div>
                                <div className="date-card-slots">
                                    {isAvailable ? (
                                        <span className="slot-count">{texts.slotsAvailable(slotCount)}</span>
                                    ) : (
                                        <span className="slot-unavailable">{texts.unavailable}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Daha Fazla / Daha Az Butonu */}
                {hasMoreDates && (
                    <div className="show-more-container">
                        <button
                            className="show-more-button"
                            onClick={toggleShowAllDates}
                        >
                            {showAllDates ? (
                                <>
                                    <ChevronUp size={20} />
                                    <span>{texts.showLess}</span>
                                </>
                            ) : (
                                <>
                                    <ChevronDown size={20} />
                                    <span>{texts.showMoreDates(sortedDates.length - MAX_INITIAL_DATES)}</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Slot selection view
    return (
        <div className="calendar-wrapper">
            <div className="calendar-header">
                <button
                    className="back-button"
                    onClick={handleBackToDateSelection}
                >
                    <ChevronLeft size={20} />
                    <span>{texts.back}</span>
                </button>
                <div className="calendar-header-content">
                    <div className="calendar-header-icon">
                        <Clock size={20} />
                    </div>
                    <div>
                        <h3 className="calendar-header-title">{texts.selectTime}</h3>
                        <p className="calendar-header-subtitle">
                            {selectedDate && getFormattedDate(selectedDate)} - {selectedDate && getDayName(selectedDate)}
                        </p>
                    </div>
                </div>
            </div>

            <div className="slots-grid">
                {selectedDate && scheduleData[selectedDate]?.map((time, i) => (
                    <button
                        key={i}
                        className="slot-button"
                        onClick={() => handleTimeButtonClick(time)}
                    >
                        <Clock size={16} />
                        <span>{time}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default AppointmentCalendar;
